/**
 * Utility functions for determining analysis status display properties
 */

export interface AnalysisStatusProps {
  analysisProgress: number;
  analysisStatusValue: string;
}

export interface AnalysisDisplayState {
  icon: string;
  iconColor: string;
  textColor: string;
  message: string;
  progressBarColor: string;
  showDismiss: boolean;
  backgroundColor: string;
  borderColor: string;
}

/**
 * Determines if the analysis has failed
 */
export const isAnalysisFailed = (statusValue: string): boolean => {
  return statusValue === 'failed';
};

/**
 * Determines if the analysis is complete
 */
export const isAnalysisComplete = (
  progress: number,
  statusValue: string,
): boolean => {
  return progress === 100 && statusValue !== 'failed';
};

/**
 * Gets the icon class name for the current analysis state
 */
export const getAnalysisIcon = ({
  analysisProgress,
  analysisStatusValue,
}: AnalysisStatusProps): string => {
  const isFailed = isAnalysisFailed(analysisStatusValue);
  const isComplete = isAnalysisComplete(analysisProgress, analysisStatusValue);

  if (isFailed) return 'pi pi-times-circle';
  if (isComplete) return 'pi pi-check-circle';
  return 'pi pi-cog pi-spin';
};

/**
 * Gets the icon color CSS variable for the current analysis state
 */
export const getAnalysisIconColor = ({
  analysisProgress,
  analysisStatusValue,
}: AnalysisStatusProps): string => {
  const isFailed = isAnalysisFailed(analysisStatusValue);
  const isComplete = isAnalysisComplete(analysisProgress, analysisStatusValue);

  if (isFailed) return 'var(--bq-color-error)';
  if (isComplete) return 'var(--bq-color-sucess)';
  return 'var(--bq-color-info)';
};

/**
 * Gets the text color CSS variable for the current analysis state
 */
export const getAnalysisTextColor = ({
  analysisProgress,
  analysisStatusValue,
}: AnalysisStatusProps): string => {
  const isFailed = isAnalysisFailed(analysisStatusValue);
  const isComplete = isAnalysisComplete(analysisProgress, analysisStatusValue);

  if (isFailed) return 'var(--bq-color-error)';
  if (isComplete) return 'var(--bq-color-sucess)';
  return 'var(--bq-color-info)';
};

/**
 * Gets the status message for the current analysis state
 */
export const getAnalysisMessage = ({
  analysisProgress,
  analysisStatusValue,
}: AnalysisStatusProps): string => {
  const isFailed = isAnalysisFailed(analysisStatusValue);
  const isComplete = isAnalysisComplete(analysisProgress, analysisStatusValue);

  if (isFailed) return 'Analysis Failed';
  if (isComplete) return 'Analysis Complete';
  return 'Analyzing';
};

/**
 * Gets the progress bar color for the current analysis state
 */
export const getAnalysisProgressBarColor = ({
  analysisProgress,
  analysisStatusValue,
}: AnalysisStatusProps): string => {
  const isFailed = isAnalysisFailed(analysisStatusValue);
  const isComplete = isAnalysisComplete(analysisProgress, analysisStatusValue);

  if (isFailed) return 'var(--bq-color-error)';
  if (isComplete) return 'var(--bq-color-sucess)';
  return 'var(--bq-color-primary)';
};

/**
 * Determines if the dismiss button should be shown
 */
export const shouldShowDismiss = ({
  analysisProgress,
  analysisStatusValue,
}: AnalysisStatusProps): boolean => {
  const isFailed = isAnalysisFailed(analysisStatusValue);
  const isComplete = isAnalysisComplete(analysisProgress, analysisStatusValue);

  return isFailed || isComplete;
};

/**
 * Gets the background color for the current analysis state
 */
export const getAnalysisBackgroundColor = ({
  analysisProgress,
  analysisStatusValue,
}: AnalysisStatusProps): string => {
  const isFailed = isAnalysisFailed(analysisStatusValue);
  const isComplete = isAnalysisComplete(analysisProgress, analysisStatusValue);

  if (isFailed) return 'var(--bq-color-error-lighter)';
  if (isComplete) return 'var(--bq-color-sucess-lighter)';
  return 'var(--bq-color-info-lighter)';
};

/**
 * Gets the border color for the current analysis state
 */
export const getAnalysisBorderColor = ({
  analysisProgress,
  analysisStatusValue,
}: AnalysisStatusProps): string => {
  const isFailed = isAnalysisFailed(analysisStatusValue);
  const isComplete = isAnalysisComplete(analysisProgress, analysisStatusValue);

  if (isFailed) return 'var(--bq-color-error)';
  if (isComplete) return 'var(--bq-color-sucess)';
  return 'var(--bq-color-info)';
};

/**
 * Gets all display properties for the current analysis state
 */
export const getAnalysisDisplayState = (
  props: AnalysisStatusProps,
): AnalysisDisplayState => {
  return {
    icon: getAnalysisIcon(props),
    iconColor: getAnalysisIconColor(props),
    textColor: getAnalysisTextColor(props),
    message: getAnalysisMessage(props),
    progressBarColor: getAnalysisProgressBarColor(props),
    showDismiss: shouldShowDismiss(props),
    backgroundColor: getAnalysisBackgroundColor(props),
    borderColor: getAnalysisBorderColor(props),
  };
};
